import React, {
  useEffect,
  useReducer,
  ComponentType,
  useState,
  useCallback,
} from 'react';
import { OBTypes } from '../constants/onBoarding';
import OBFrame from '../components/ob/OBFrame';
import ProfileView from '../views/ProfileView';
import PersonalizeView from '../views/PersonalizeView';
import { APP_SECTIONS_PATHS } from '../constants/routes';
import OrganizationService from '../services/OrganizationService';
import CenterService from '../services/CenterService';
import useCenters from '../hooks/centers/UseCenters';
import useAccount from '../hooks/useAccount';
import EventReporter from 'appEvents';
import { AppEvents, EventProviders } from 'constants/events';
import i18n from 'i18next';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import { setLocalStorageItem } from 'lib/helpers';
import { prepareData } from './functions';
import { mixpanel } from 'appEvents/providers';
import useOrganization from '../hooks/UseOrganization';

export interface ObContainerProps {
  OBType?: string,
  useReducer?: any,
  history: any,
  setDoingOb?: any,
}

export const OBStepStatus = {
  PENDING: 'pending',
  DOING: 'doing',
  DONE: 'done',
};

export interface IStep {
  label: string;
  view: ComponentType<any>;
  status: string;
}

interface IState {
  currentStepIndex: number;
  steps?: IStep[];
}

// interface IAction {
//   type: string;
//   steps?: IStep[];
//   stepIndex?: number,
// }

const UserStep = {
  label: 'CREATE_YOUR_PROFILE',
  view: ProfileView,
  status: OBStepStatus.PENDING,
};

const PersonalizeStep = {
  label: 'TELL_US_MORE',
  view: PersonalizeView,
  status: OBStepStatus.PENDING,
};

const OBSteps = {
  [OBTypes.FULL]: [
    UserStep,
    PersonalizeStep,
  ],
};

const initialState = {
  currentStepIndex: 0,
  steps: [],
};

const reducer = (state: IState, action: any): IState => {
  switch (action.type) {
  case 'INIT_STEPS':
    return { ...state, steps: action.steps };
  case 'NEXT_STEP':
    return { ...state, currentStepIndex: state.currentStepIndex + 1 };
  case 'GO_TO_STEP':
    return { ...state, currentStepIndex: action.stepIndex };
  default:
    throw new Error();
  }
};

export interface IformData {
  activities_per_day?: number;
  dap_initial_day?: number;
  name?: string;
  lastname?: string;
  plan_days?: number;
  schoolname?: string;
  title?: string;
}

const ObContainer: React.FC<ObContainerProps> = ({
  OBType = OBTypes.FULL,
  history,
  setDoingOb,
}) => {
  const { user } = useAccount();
  const { orgToken } = useOrganization();
  const initialFormData = { name: user.name, lastname: user.lastname };
  const [{ steps = [], currentStepIndex }, dispatch] = useReducer(reducer, initialState);
  const [formData, setFormData] = useState<IformData>(initialFormData);
  const CurrentStep = steps[currentStepIndex];
  const orgService = OrganizationService.getInstance();
  const centerService = CenterService.getInstance();
  const firstCenterId = Number(localStorage.getItem('account_first_center_id'));
  const landingLang = localStorage.getItem('LANDING_LOCALE');
  setLocalStorageItem(KEYS_LOCALSTORAGE.USER_TYPE, 'Owner');
  useCenters();

  const initSteps = () => {
    const obSteps = OBSteps[OBType];
    obSteps[0].status = OBStepStatus.DOING;
    dispatch({ type: 'INIT_STEPS', steps: [...obSteps] });
  };

  useEffect(() => {
    setFormData({ ...formData, ...initialFormData });
    /* eslint-disable-next-line */
  }, [initialFormData.name]);

  const submitValues = useCallback(async (form) => {
    const {
      ownerData,
      organizationData,
      centerData,
      centerConfig,
      schoolName
    } = prepareData(form);

    if (schoolName) {
      localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_NAME, schoolName);
      EventReporter.setUserAttributes({ organization_name: schoolName });
    }

    const processes = [
      orgService.updateOwner(ownerData),
      orgService.updateOrganization(organizationData),
    ];

    const updatedCenter = await centerService.updateCenter(firstCenterId, centerData);
    const updatedCenterToken = updatedCenter?.kinedu_classrooms_token?.access_token;

    processes.push(centerService.addCenterConfig(centerConfig, updatedCenterToken));

    Promise.all(processes).then(() => {
      localStorage.removeItem('doing_ob');
      setDoingOb(false);
      orgService.updateConfig({ organization_configuration: centerConfig.center_configuration }, orgToken);

      EventReporter.action(AppEvents.COMPLETE_REGISTRATION(), [EventProviders.FB_PIXEL]);

      return history.push(APP_SECTIONS_PATHS.DAP);
    }).catch((err) => {
      console.error('%c🚫 Promise err', 'background: #a20300; color: #ffa8a6; font-size: 16px', err);
    });
    /* eslint-disable-next-line */
  }, [formData]);

  const trackEvents = (stepIndex: number, form: any) => {
    let organizationName = '';
    if (form.schoolname) {
      organizationName = form.schoolname;

      // Set the new organization name in localstorage
      if (organizationName) {
        localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_NAME, organizationName);
      }

      // After we have collected the school name we should update the mixpanel properties.
      EventReporter.setUserAttributes({
        organization_name: organizationName,
      });

      mixpanel.getInstance().register({
        organization_name: organizationName,
      });
    }

    if (form.name || form.lastname) {
      EventReporter.setUserAttributes({
        name: form.name || '',
        lastname: form.lastname || '',
      });
    }

    /**
     * We had to add a delay here to identify the new changes in the user properties correctly.
     */
    setTimeout(() => {
      switch (stepIndex) {
      case 0: {
        EventReporter.view(AppEvents.CREATE_PROFILE({ organization_name: organizationName }));
        break;
      }

      case 1: {
        EventReporter.view(AppEvents.CREATE_PERSONALIZED_PLAN({
          organization_name: organizationName,
        }));
        break;
      }
      default: return false;
      }
    }, 1000);
  };

  const nextStep = (values: {}) => {
    const form = { ...formData, ...values };
    const hasNext = steps.length > currentStepIndex + 1;

    setFormData(form);
    trackEvents(currentStepIndex, form);

    if (hasNext) {
      steps[currentStepIndex].status = OBStepStatus.DONE;
      steps[currentStepIndex + 1].status = OBStepStatus.DOING;
      dispatch({ type: 'NEXT_STEP' });
    } else {
      submitValues(form);
    }

    return false;
  };

  const goToStep = (index: number) => {
    steps[currentStepIndex].status = OBStepStatus.DONE;
    steps[index].status = OBStepStatus.DOING;
    dispatch({ type: 'GO_TO_STEP', stepIndex: index });
  };

  useEffect(() => {
    initSteps();
    i18n.changeLanguage(landingLang || 'en');
    /* eslint-disable-next-line */
  }, []);

  if (!CurrentStep) return null;

  return (
    <OBFrame steps={steps} goToStep={goToStep}>
      <CurrentStep.view
        formData={formData}
        nextStep={nextStep}
      />
    </OBFrame>
  );
};

export default ObContainer;
